import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../design-system';
import { createPopup } from '@typeform/embed';
import '@typeform/embed/build/css/popup.css';

interface NavigationProps {
  noBorder?: boolean;
}

const Navigation: React.FC<NavigationProps> = ({ noBorder = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navLinks = [
    { name: 'About', path: '/about' },

    { name: 'Contact', path: '/contact' },
  ];

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const togglePopup = () => {
    const { toggle } = createPopup('QXs6zOPX');
    toggle();
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 bg-dark-900 ${noBorder ? '' : 'border-b border-white/5'}`} style={{ transform: 'translateZ(0)', WebkitBackfaceVisibility: 'hidden', backfaceVisibility: 'hidden', contain: 'layout style paint' }}>
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-18">
            {/* Logo */}
            <div className="flex items-center flex-shrink-0">
              <Link to="/" className="flex items-center group" onClick={closeMenu}>
                {/* Desktop/Wide Screen Logo - Logo with text */}
                <img
                  src="/incupie_wide.svg"
                  alt="Incupie"
                  className="hidden w-auto h-8 transition-all duration-300 md:block lg:h-10 group-hover:scale-105"
                />
                {/* Mobile Logo - Logo only, no text */}
                <img
                  src="/incupie.svg"
                  alt="Incupie"
                  className="block w-8 h-8 transition-all duration-300 md:hidden group-hover:scale-105"
                />
              </Link>
            </div>

            {/* Desktop Navigation - Centered */}
            <div className="items-center justify-center flex-1 hidden lg:flex">
              <div className="flex items-center space-x-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`transition-colors duration-300 font-medium ${location.pathname === link.path ? 'text-primary-400' : 'text-white/70 hover:text-white'
                      }`}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Action buttons */}
            <div className="items-center flex-shrink-0 hidden gap-4 lg:flex">

              <Button as="button" onClick={togglePopup} size="sm">
                Join Waitlist
              </Button>

            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <button
                onClick={toggleMenu}
                className="p-2 transition-colors duration-300 text-white/70 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Sidebar Overlay */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] transition-opacity duration-300 lg:hidden"
          onClick={closeMenu}
        />
      )}

      {/* Mobile Sidebar */}
      <div className={`lg:hidden fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-dark-900 border-l border-white/10 shadow-2xl transform transition-transform duration-300 ease-in-out z-[70] ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center">
              <img src="/incupie.svg" alt="Incupie" className="w-8 h-8" />
              <span className="ml-3 text-lg font-semibold text-white">Menu</span>
            </div>
            <button
              onClick={closeMenu}
              className="p-2 transition-colors duration-300 rounded-lg text-white/70 hover:text-white hover:bg-white/5"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Navigation Links */}
          <div className="flex-1 px-4 py-6">
            <nav className="space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`block px-4 py-3 rounded-lg transition-all duration-300 font-medium ${location.pathname === link.path
                    ? 'text-primary-400 bg-primary-400/10 border border-primary-400/20'
                    : 'text-white/70 hover:text-white hover:bg-white/5'
                    }`}
                  onClick={closeMenu}
                >
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Action Buttons */}
          <div className="p-4 space-y-3 border-t border-white/10">

            <Button as="button" onClick={() => { closeMenu(); togglePopup(); }} size="md" className="justify-center w-full">
              Join Waitlist
            </Button>

          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
